'use client';

import { useMemo } from 'react';
import { usePathname, useSearchParams } from '@/lib/app-router';
import { AUTH_ROUTES } from '@/routes/routes.constants';

/**
 * Builds the `/login` href that returns the visitor to the page they left.
 *
 * The scan-surface producers that already do this by hand (`useQrBookingPage`'s `onGuestSignIn`,
 * the touchless page's `handleSignIn`) each build the same string from a click handler. This hook
 * exists for the entry points declared as STATIC nav items, which had nowhere to run that code —
 * the app-shell header's guest "Sign In", on both the desktop nav and the mobile guest action.
 *
 * ## Invariants:
 * - The return URL carries the QUERY STRING as well as the path. Why: a scanned link holds the
 *   company and direction as params (`/z/412?c=ACME&d=i`); returning to the bare path drops them
 *   and the scan can no longer resolve which company the device belongs to. This is the same
 *   invariant `onGuestSignIn` carries, and the reason a plain `href: '/login'` is a bug rather
 *   than a missing nicety (PB-4971).
 * - Built from `usePathname()` re-exported by `@/lib/app-router`, which STRIPS the
 *   `/c/{companyId}` prefix — matching every existing producer. The proxy's
 *   `resolvePostLoginTarget` re-prefixes the company base only when the value does not already
 *   carry it, so a stripped path and a full path both resolve; keeping ONE shape across producers
 *   is what leaves that conditional exercised in one direction only.
 * - `minimal` defaults to FALSE here, unlike the scan producers which pass `true`. Why: those
 *   interrupt a booking mid-flow, where the login page's marketing panel is noise. The app-shell
 *   header is the ordinary way into login from a public page, and forcing minimal there would
 *   restyle the login screen for every visitor — a visual change this fixes no part of.
 *
 * @param options.minimal when true, appends `minimal=true` to hide the login page's branding panel
 * @returns a login path carrying an encoded `returnUrl` for the current location
 */
export function useLoginHref(options?: { minimal?: boolean }): string {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const minimal = options?.minimal ?? false;

  return useMemo(() => {
    const query = searchParams?.toString();
    const returnUrl = query ? `${pathname}?${query}` : pathname;
    const params = new URLSearchParams({ returnUrl });
    if (minimal) {
      params.set('minimal', 'true');
    }
    return `${AUTH_ROUTES.login}?${params.toString()}`;
  }, [pathname, searchParams, minimal]);
}
